import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { BreakpointSize, breakpointSizePx } from '@jbhi-fi/lanyard-ui/themes/breakpoints';
import { getBreakpointValue } from '@jbhi-fi/utils/src/breakpointHelpers/breakpointHelpers';

type CurrentBreakpoint = {
  name: BreakpointSize;
  value: number;
};

const getCurrentBreakpoint = (screenWidth: number): CurrentBreakpoint => {
  const currentBreakpoint = getBreakpointValue(screenWidth);
  return {
    name: currentBreakpoint,
    value: breakpointSizePx[currentBreakpoint],
  };
};

const CurrentBreakpointContext = createContext<CurrentBreakpoint | undefined>(undefined);

export const CurrentBreakpointProvider = ({ children }: { children: ReactNode }) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<CurrentBreakpoint>(() => {
    // Default to 'xs' for server-side rendering.
    if (typeof window !== 'undefined') {
      return getCurrentBreakpoint(window.innerWidth);
    }
    return { value: 0, name: 'xs' };
  });

  useEffect(() => {
    const handleResize = () => {
      const newBreakpoint = getCurrentBreakpoint(window.innerWidth);
      setCurrentBreakpoint(prevBreakpoint =>
        prevBreakpoint.value !== newBreakpoint.value ? newBreakpoint : prevBreakpoint
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return <CurrentBreakpointContext.Provider value={currentBreakpoint}>{children}</CurrentBreakpointContext.Provider>;
};

export const useCurrentBreakpoint = (): CurrentBreakpoint => {
  const context = useContext(CurrentBreakpointContext);
  if (context === undefined) {
    throw new Error('useCurrentBreakpoint must be used within a CurrentBreakpointProvider');
  }

  return context;
};
